import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import type { TripItineraryData, TripStop } from 'tripPlanner/api/types'
import type { HotelRoomOption } from 'tripPlanner/types/hotelRoomOption'
import { getOfferItineraryDayRange, findOfferStopInItinerary, findNearestStop, type Coordinates } from 'tripPlanner/utils/chatItinerary'
import { getDayLabel, getLocalisedDayLabel } from 'tripPlanner/utils/chatDates'
import { appendContextTag, TRIP_PLANNER_ACTION_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'
import type { SelectedDayContext } from 'tripPlanner/utils/pendingExperienceDaySelection'
import noop from 'lib/function/noop'

type ItineraryStatus =
  | { type: 'not-added' }
  | { type: 'to-be-planned'; label: string }
  | { type: 'days'; label: string }

export interface SendMessageOptions {
  offerId?: string
  offerType?: string
  selectedDayContext?: SelectedDayContext
  roomOption?: HotelRoomOption
}

interface Args {
  itineraryData?: TripItineraryData
  hotelName: string
  offerId: string
  offerType?: string
  hotelCity?: string
  hotelCoordinates?: Coordinates
  /** When provided (e.g. from the hotel listing drawer), used as the
   *  preferred stop instead of falling back to coordinate-based matching. */
  contextStop?: TripStop
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  onAfterAction?: () => void
  filterNights?: number
  checkIn?: string
  checkOut?: string
  roomOption?: HotelRoomOption
}

interface Result {
  handleAddToTrip: () => void
  handleSelectRoomOption: () => void
  isModalOpen: boolean
  closeModal: () => void
  itineraryStatus: ItineraryStatus
  relevantStop?: TripStop
  defaultDayNumber: number
  defaultNights: number
  bookingRoomOption?: HotelRoomOption
}

const NOT_ADDED_STATUS: ItineraryStatus = { type: 'not-added' }

function useAddHotelToTrip({
  itineraryData,
  hotelName,
  offerId,
  offerType,
  hotelCity,
  hotelCoordinates,
  contextStop,
  onSendMessage = noop,
  onAfterAction,
  filterNights,
  checkIn,
  checkOut,
  roomOption,
}: Args): Result {
  const intl = useIntl()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const offerDayRange = useMemo(
    () => getOfferItineraryDayRange(offerId, itineraryData),
    [offerId, itineraryData],
  )

  const itineraryStatus: ItineraryStatus = offerDayRange
    ? { type: offerDayRange.type, label: offerDayRange.label }
    : NOT_ADDED_STATUS

  const defaultDayNumber = offerDayRange?.firstDayNumber ?? 1
  const defaultNights = offerDayRange?.nightsCount ?? filterNights ?? 1
  const bookingCheckIn = checkIn ?? roomOption?.checkIn
  const bookingCheckOut = checkOut ?? roomOption?.checkOut

  // Merges stop/selection dates into the roomOption so the backend context block
  // always carries explicit checkIn/checkOut regardless of how the button was triggered.
  const bookingRoomOption = useMemo<HotelRoomOption | undefined>(() => {
    if (!roomOption && !bookingCheckIn && !bookingCheckOut) return undefined
    return { ...roomOption, checkIn: bookingCheckIn, checkOut: bookingCheckOut }
  }, [roomOption, bookingCheckIn, bookingCheckOut])

  const relevantStop = useMemo<TripStop | undefined>(() => {
    const offerStop = findOfferStopInItinerary(offerId, itineraryData)
    if (offerStop) return offerStop

    if (contextStop) return contextStop

    if (!itineraryData?.stops) return undefined
    return findNearestStop(hotelCoordinates, itineraryData.stops)
  }, [offerId, itineraryData, contextStop, hotelCoordinates])

  const handleAddToTrip = useCallback(() => {
    const stops = itineraryData?.stops ?? []
    const isAlreadyInTrip = itineraryStatus.type !== 'not-added'

    if (isAlreadyInTrip) {
      setIsModalOpen(true)
      return
    }

    if (stops.length === 0) {
      const displayMessage = hotelCity
        ? intl.formatMessage(
            {
              id: 'RMB55Nt73KQmOgLYwQ6B',
              defaultMessage: 'Add "{hotelName}" to be planned in {cityName}',
              description: 'User chat message shown when adding a hotel to be planned in a specific city',
            },
            {
              hotelName,
              cityName: hotelCity,
            },
          )
        : intl.formatMessage({
            id: '9EdNRsLnq6gg6QYmWJW-',
            defaultMessage: 'Add "{hotelName}" to be planned',
            description: 'User chat message shown when adding a hotel to the unscheduled part of a trip with no stops',
          }, { hotelName })
      const cityLabel = hotelCity ? ` in ${hotelCity}` : ''
      onSendMessage(
        appendContextTag(
          displayMessage,
          `Add "${hotelName}" to be planned${cityLabel}`,
          TRIP_PLANNER_ACTION_CONTEXT_LABEL,
        ),
        { offerId, offerType, roomOption: bookingRoomOption },
      )
      onAfterAction?.()
      return
    }

    const hasAnyDays = stops.some(stop => stop.days.length > 0)
    if (!hasAnyDays) {
      const targetStop = relevantStop ?? stops[0]
      onSendMessage(
        appendContextTag(
          intl.formatMessage(
            {
              id: 'qmRNkszmMJ0jG9qinud4',
              defaultMessage: 'Add "{hotelName}" to be planned for {stopName}',
              description: 'User chat message shown when adding a hotel to the unscheduled part of a stop with no dated days',
            },
            {
              hotelName,
              stopName: targetStop.placeName,
            },
          ),
          `Add "${hotelName}" to be planned for ${targetStop.placeName}`,
          TRIP_PLANNER_ACTION_CONTEXT_LABEL,
        ),
        { offerId, offerType, roomOption: bookingRoomOption },
      )
      onAfterAction?.()
      return
    }

    // When checkIn/checkOut are known (e.g. from room selection), skip the modal
    // and send the assignment message directly using the actual dates.
    if (bookingCheckIn && bookingCheckOut) {
      const nights = moment(bookingCheckOut).diff(moment(bookingCheckIn), 'days')
      if (nights > 0) {
        const cityName = relevantStop?.placeName ?? hotelCity ?? ''
        const fromDisplayLabel = getLocalisedDayLabel(intl, 1, bookingCheckIn)
        const fromContextLabel = getDayLabel(1, bookingCheckIn)
        const toDisplayLabel = getLocalisedDayLabel(intl, 1 + nights, bookingCheckOut)
        const toContextLabel = getDayLabel(1 + nights, bookingCheckOut)
        const displayMessage = intl.formatMessage(
          {
            id: '05Xk8n84P0XUpCggECPh',
            defaultMessage: 'Assign "{hotelName}" to stay from {fromLabel} to {toLabel} in {cityName}',
            description: 'User chat message shown when assigning a hotel stay to a date range in a specific city',
          },
          { hotelName, fromLabel: fromDisplayLabel, toLabel: toDisplayLabel, cityName },
        )
        onSendMessage(
          appendContextTag(
            displayMessage,
            `Assign "${hotelName}" to stay from ${fromContextLabel} to ${toContextLabel} in ${cityName}`,
            TRIP_PLANNER_ACTION_CONTEXT_LABEL,
          ),
          { offerId, offerType, roomOption: bookingRoomOption },
        )
        onAfterAction?.()
        return
      }
    }

    setIsModalOpen(true)
  }, [intl, itineraryData?.stops, itineraryStatus.type, hotelName, hotelCity, offerId, offerType, onSendMessage, onAfterAction, relevantStop, bookingCheckIn, bookingCheckOut, bookingRoomOption])

  const handleSelectRoomOption = useCallback(() => {
    if (itineraryStatus.type === 'not-added') {
      handleAddToTrip()
      return
    }

    const packageName = bookingRoomOption?.packageName ?? hotelName
    const displayMessage = bookingCheckIn && bookingCheckOut
      ? intl.formatMessage(
          {
            id: 'FmO_HtT01H10BlHif1U0',
            defaultMessage: 'Update "{hotelName}" to {packageName} from {checkInDate} to {checkOutDate}',
            description: 'User chat message sent when updating a hotel already in the trip with a selected room package and date range',
          },
          {
            hotelName,
            packageName,
            checkInDate: bookingCheckIn,
            checkOutDate: bookingCheckOut,
          },
        )
      : intl.formatMessage(
          {
            id: '-eDrLZpV3XZM0AA0FTPb',
            defaultMessage: 'Update "{hotelName}" to {packageName}',
            description: 'User chat message sent when updating a hotel already in the trip with a selected room package',
          },
          {
            hotelName,
            packageName,
          },
        )
    const contextMessage = bookingCheckIn && bookingCheckOut
      ? `Update "${hotelName}" to ${packageName} from ${bookingCheckIn} to ${bookingCheckOut}`
      : `Update "${hotelName}" to ${packageName}`

    onSendMessage(
      appendContextTag(
        displayMessage,
        contextMessage,
        TRIP_PLANNER_ACTION_CONTEXT_LABEL,
      ),
      { offerId, offerType, roomOption: bookingRoomOption },
    )
    onAfterAction?.()
  }, [bookingRoomOption, bookingCheckIn, bookingCheckOut, handleAddToTrip, hotelName, intl, itineraryStatus.type, offerId, offerType, onAfterAction, onSendMessage])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  return {
    handleAddToTrip,
    handleSelectRoomOption,
    isModalOpen,
    closeModal,
    itineraryStatus,
    relevantStop,
    defaultDayNumber,
    defaultNights,
    bookingRoomOption,
  }
}

export default useAddHotelToTrip
